import IdDocType from '@/resources/enum/IdDocType';

/**
 * 書類ごとの種類・形状・撮影方法をまとめる
 */

type IdDocShape = 'book' | 'card' | 'a4' | 'mnGeneral' | 'gpGeneral' | 'unknown';

type IdDocTableItem = {
  idDocType: IdDocType;
  shape: IdDocShape;
  isGlobal?: boolean;
  isGeneral?: boolean; // 他書類追加（ト・チ対応 web/SDK経由撮影方式・親子確認対応）にて追加された書類 / パスポート（全期間）は除く @see https://liquidinc.atlassian.net/wiki/spaces/EKYC/pages/2169012242/web+SDK @see https://liquidinc.atlassian.net/wiki/spaces/EKYC/pages/2817820081
  isFaceRequired?: boolean; // 顔認証が必要かどうか
  skipDiagonal?: boolean; // 斜め撮影をスキップするか
  skipBack?: boolean; // 裏面（二枚目）撮影をスキップするか
};

type IdDocGroup = keyof typeof idDocTable;

export const idDocTable = {
  unknown: [
    {
      idDocType: IdDocType.Unknown,
      shape: 'unknown'
    },
    /**
     * 個人番号・汎用書類
     */

    {
      idDocType: IdDocType.MnGeneral,
      shape: 'mnGeneral'
    },
    {
      idDocType: IdDocType.GpGeneral,
      shape: 'gpGeneral'
    }
  ],
  'drivers-license': [
    {
      idDocType: IdDocType.DriversLicenseHo,
      shape: 'card',
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.DriversLicenseTo,
      shape: 'card'
    },
    {
      idDocType: IdDocType.DriversLicenseTi,
      shape: 'card'
    },
    {
      idDocType: IdDocType.DriversLicenseFrontFace,
      shape: 'card',
      isFaceRequired: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.DriversLicenseFront,
      shape: 'card',
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.DriversLicenseFrontBackFace,
      shape: 'card',
      skipDiagonal: true,
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.DriversLicenseFrontBack,
      shape: 'card',
      skipDiagonal: true
    }
  ],
  'my-number-card': [
    {
      idDocType: IdDocType.MyNumberCardHo,
      shape: 'card',
      isFaceRequired: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.MyNumberCardTo,
      shape: 'card',
      skipBack: true
    },
    {
      idDocType: IdDocType.MyNumberCardTi,
      shape: 'card',
      skipBack: true
    },
    {
      idDocType: IdDocType.MyNumberCardFrontFace,
      shape: 'card',
      isFaceRequired: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.MyNumberCardAndBack,
      shape: 'card',
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.MyNumberCardFront,
      shape: 'card',
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.MyNumberCardFrontBackFace,
      shape: 'card',
      skipDiagonal: true,
      isFaceRequired: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.MyNumberCardFrontBack,
      shape: 'card',
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'residence-card': [
    {
      idDocType: IdDocType.ResidenceCardHo,
      shape: 'card',
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.ResidenceCardTo,
      shape: 'card'
    },
    {
      idDocType: IdDocType.ResidenceCardTi,
      shape: 'card'
    },
    {
      idDocType: IdDocType.ResidenceCardFrontFace,
      shape: 'card',
      isFaceRequired: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.ResidenceCardFront,
      shape: 'card',
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.ResidenceCardFrontBackFace,
      shape: 'card',
      skipDiagonal: true,
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.ResidenceCardFrontBack,
      shape: 'card',
      skipDiagonal: true
    }
  ],
  passport: [
    {
      idDocType: IdDocType.PassportHo,
      shape: 'book',
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.PassportTo,
      shape: 'book'
    },
    {
      idDocType: IdDocType.PassportTi,
      shape: 'book'
    },
    {
      idDocType: IdDocType.PassportFrontFace,
      shape: 'book',
      isFaceRequired: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.PassportFront,
      shape: 'book',
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.PassportFrontBackFace,
      shape: 'book',
      skipDiagonal: true,
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.PassportFrontBack,
      shape: 'book',
      skipDiagonal: true
    }
  ],
  'special-permanent-resident-certificate': [
    {
      idDocType: IdDocType.SpecialPermanentResidentCertificateHo,
      shape: 'card',
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.SpecialPermanentResidentCertificateTo,
      shape: 'card'
    },
    {
      idDocType: IdDocType.SpecialPermanentResidentCertificateTi,
      shape: 'card'
    },
    {
      idDocType: IdDocType.SpecialPermanentResidentCertificateFrontFace,
      shape: 'card',
      isFaceRequired: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.SpecialPermanentResidentCertificateFront,
      shape: 'card',
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.SpecialPermanentResidentCertificateFrontBackFace,
      shape: 'card',
      skipDiagonal: true,
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.SpecialPermanentResidentCertificateFrontBack,
      shape: 'card',
      skipDiagonal: true
    }
  ],
  'driving-history-certificate': [
    {
      idDocType: IdDocType.DrivingHistoryCertificateHo,
      shape: 'card',
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.DrivingHistoryCertificateTo,
      shape: 'card'
    },
    {
      idDocType: IdDocType.DrivingHistoryCertificateTi,
      shape: 'card'
    },
    {
      idDocType: IdDocType.DrivingHistoryCertificateFrontFace,
      shape: 'card',
      isFaceRequired: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.DrivingHistoryCertificateFront,
      shape: 'card',
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.DrivingHistoryCertificateFrontBackFace,
      shape: 'card',
      skipDiagonal: true,
      isFaceRequired: true
    },
    {
      idDocType: IdDocType.DrivingHistoryCertificateFrontBack,
      shape: 'card',
      skipDiagonal: true
    }
  ],
  'health-insurance-card': [
    {
      idDocType: IdDocType.HealthInsuranceCardTo,
      shape: 'card'
    },
    {
      idDocType: IdDocType.HealthInsuranceCardTi,
      shape: 'card'
    },
    {
      idDocType: IdDocType.HealthInsuranceCardFront,
      shape: 'card',
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.HealthInsuranceCardFrontBack,
      shape: 'card',
      skipDiagonal: true
    }
  ],
  'passport-any-issuance-date': [
    {
      idDocType: IdDocType.PassportAnyIssuanceDateTo,
      shape: 'book'
    },
    {
      idDocType: IdDocType.PassportAnyIssuanceDateTi,
      shape: 'book'
    },
    {
      idDocType: IdDocType.PassportAnyIssuanceDateFront,
      shape: 'book',
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.PassportAnyIssuanceDateFrontFace,
      shape: 'book',
      isFaceRequired: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.PassportAnyIssuanceDateFrontBack,
      shape: 'book',
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.PassportAnyIssuanceDateFrontBackFace,
      shape: 'book',
      skipDiagonal: true,
      isFaceRequired: true
    }
  ],
  /**
   * 他書類追加（ト・チ対応 web/SDK経由撮影方式）にて追加された書類
   * パスポート（全期間）は除く
   * @see https://liquidinc.atlassian.net/wiki/spaces/EKYC/pages/2169012242/web+SDK
   */
  'physical-disability-certificate-book': [
    {
      idDocType: IdDocType.PhysicalDisabilityCertificateBookTo,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.PhysicalDisabilityCertificateBookTi,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.PhysicalDisabilityCertificateBookFront,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.PhysicalDisabilityCertificateBookFrontBack,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'physical-disability-certificate-card': [
    {
      idDocType: IdDocType.PhysicalDisabilityCertificateCardTo,
      shape: 'card',
      isGeneral: true
    },
    {
      idDocType: IdDocType.PhysicalDisabilityCertificateCardTi,
      shape: 'card',
      isGeneral: true
    },
    {
      idDocType: IdDocType.PhysicalDisabilityCertificateCardFront,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.PhysicalDisabilityCertificateCardFrontBack,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'special-education-book': [
    {
      idDocType: IdDocType.SpecialEducationBookTo,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.SpecialEducationBookTi,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.SpecialEducationBookFront,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.SpecialEducationBookFrontBack,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'special-education-card': [
    {
      idDocType: IdDocType.SpecialEducationCardTo,
      shape: 'card',
      isGeneral: true
    },
    {
      idDocType: IdDocType.SpecialEducationCardTi,
      shape: 'card',
      isGeneral: true
    },
    {
      idDocType: IdDocType.SpecialEducationCardFront,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.SpecialEducationCardFrontBack,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'mental-disability-certificate-book': [
    {
      idDocType: IdDocType.MentalDisabilityCertificateBookTo,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.MentalDisabilityCertificateBookTi,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.MentalDisabilityCertificateBookFront,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.MentalDisabilityCertificateBookFrontBack,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'mental-disability-certificate-card': [
    {
      idDocType: IdDocType.MentalDisabilityCertificateCardTo,
      shape: 'card',
      isGeneral: true
    },
    {
      idDocType: IdDocType.MentalDisabilityCertificateCardTi,
      shape: 'card',
      isGeneral: true
    },
    {
      idDocType: IdDocType.MentalDisabilityCertificateCardFront,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.MentalDisabilityCertificateCardFrontBack,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'pension-book': [
    {
      idDocType: IdDocType.PensionBookTo,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.PensionBookTi,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.PensionBookFront,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.PensionBookFrontBack,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'basic-pension-number-notification': [
    {
      idDocType: IdDocType.BasicPensionNumberNotificationTo,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.BasicPensionNumberNotificationTi,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.BasicPensionNumberNotificationFront,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.BasicPensionNumberNotificationFrontBack,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'basic-resident-registration-card': [
    {
      idDocType: IdDocType.BasicResidentRegistrationCardTo,
      shape: 'card',
      isGeneral: true
    },
    {
      idDocType: IdDocType.BasicResidentRegistrationCardTi,
      shape: 'card',
      isGeneral: true
    },
    {
      idDocType: IdDocType.BasicResidentRegistrationCardFrontFace,
      shape: 'card',
      isFaceRequired: true,
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.BasicResidentRegistrationCardFront,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.BasicResidentRegistrationCardFrontBackFace,
      shape: 'card',
      isFaceRequired: true,
      isGeneral: true,
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.BasicResidentRegistrationCardFrontBack,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'student-notebook': [
    {
      idDocType: IdDocType.StudentNotebookTo,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.StudentNotebookTi,
      shape: 'book',
      isGeneral: true
    },
    {
      idDocType: IdDocType.StudentNotebookFrontFace,
      shape: 'book',
      isFaceRequired: true,
      isGeneral: true,
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.StudentNotebookFront,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.StudentNotebookFrontBackFace,
      shape: 'book',
      isFaceRequired: true,
      isGeneral: true,
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.StudentNotebookFrontBack,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'student-id-card': [
    {
      idDocType: IdDocType.StudentIDCardTo,
      shape: 'card',
      isGeneral: true
    },
    {
      idDocType: IdDocType.StudentIDCardTi,
      shape: 'card',
      isGeneral: true
    },
    {
      idDocType: IdDocType.StudentIDCardFrontFace,
      shape: 'card',
      isFaceRequired: true,
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.StudentIDCardFront,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.StudentIDCardFrontBackFace,
      shape: 'card',
      isFaceRequired: true,
      isGeneral: true,
      skipDiagonal: true
    },
    {
      idDocType: IdDocType.StudentIDCardFrontBack,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true
    }
  ],
  'mother-and-baby-notebook': [
    {
      idDocType: IdDocType.MotherAndBabyNotebookFront,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.MotherAndBabyNotebookFrontBack,
      shape: 'book',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'certificate-of-residence': [
    {
      idDocType: IdDocType.CertificateOfResidenceFront,
      shape: 'a4',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.CertificateOfResidenceFrontBack,
      shape: 'a4',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'certificate-of-family-register': [
    {
      idDocType: IdDocType.CertificateOfFamilyRegisterFront,
      shape: 'a4',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.CertificateOfFamilyRegisterFrontBack,
      shape: 'a4',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'certificate-of-individual-records': [
    {
      idDocType: IdDocType.CertificateOfIndividualRecordsFront,
      shape: 'a4',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.CertificateOfIndividualRecordsFrontBack,
      shape: 'a4',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  /**
   * Global
   */
  'drivers-license-global-face': [
    {
      idDocType: IdDocType.DriversLicenseGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true
    }
  ],
  'national-identity-card-global-face': [
    {
      idDocType: IdDocType.NationalIdentityCardGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true
    }
  ],
  'residence-card-global-face': [
    {
      idDocType: IdDocType.ResidenceCardGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true
    }
  ],
  'passport-global-face': [
    {
      idDocType: IdDocType.PassportGlobalFace,
      shape: 'book',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'philid-global-face': [
    {
      idDocType: IdDocType.PhilIDGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true
    }
  ],
  'tinid-global-face': [
    {
      idDocType: IdDocType.TINIDGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'sssid-global-face': [
    {
      idDocType: IdDocType.SSSIDGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'umid-global-face': [
    {
      idDocType: IdDocType.UMIDGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'umid2-global-face': [
    {
      idDocType: IdDocType.UMID2GlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'nric-global-face': [
    {
      idDocType: IdDocType.NRICGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'work-permit-global-face': [
    {
      idDocType: IdDocType.WorkPermitGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'ktp-global-face': [
    {
      idDocType: IdDocType.KTPGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'kia-over5-global-face': [
    {
      idDocType: IdDocType.KIAOver5GlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'kitap-global-face': [
    {
      idDocType: IdDocType.KITAPGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'cccd-global-face': [
    {
      idDocType: IdDocType.CCCDGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'cmnd-global-face': [
    {
      idDocType: IdDocType.CMNDGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'mypr-global-face': [
    {
      idDocType: IdDocType.MyPRGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'mykad-global-face': [
    {
      idDocType: IdDocType.MyKADGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'aadhaar-global-face': [
    {
      idDocType: IdDocType.AadhaarGlobalFace,
      shape: 'card',
      isFaceRequired: true,
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'drivers-license-global-no-face': [
    {
      idDocType: IdDocType.DriversLicenseGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true
    }
  ],
  'national-identity-card-global-no-face': [
    {
      idDocType: IdDocType.NationalIdentityCardGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true
    }
  ],
  'residence-card-global-no-face': [
    {
      idDocType: IdDocType.ResidenceCardGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true
    }
  ],
  'passport-global-no-face': [
    {
      idDocType: IdDocType.PassportGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'philid-global-no-face': [
    {
      idDocType: IdDocType.PhilIDGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true
    }
  ],
  'tinid-global-no-face': [
    {
      idDocType: IdDocType.TINIDGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'sssid-global-no-face': [
    {
      idDocType: IdDocType.SSSIDGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'umid-global-no-face': [
    {
      idDocType: IdDocType.UMIDGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'umid2-global-no-face': [
    {
      idDocType: IdDocType.UMID2GlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'nric-global-no-face': [
    {
      idDocType: IdDocType.NRICGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'work-permit-global-no-face': [
    {
      idDocType: IdDocType.WorkPermitGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'ktp-global-no-face': [
    {
      idDocType: IdDocType.KTPGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'kia-over5-global-no-face': [
    {
      idDocType: IdDocType.KIAOver5GlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'kia-under5-global-no-face': [
    {
      idDocType: IdDocType.KIAUnder5GlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'kitap-global-no-face': [
    {
      idDocType: IdDocType.KITAPGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'cccd-global-no-face': [
    {
      idDocType: IdDocType.CCCDGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'cmnd-global-no-face': [
    {
      idDocType: IdDocType.CMNDGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'mypr-global-no-face': [
    {
      idDocType: IdDocType.MyPRGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'mykad-global-no-face': [
    {
      idDocType: IdDocType.MyKADGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'aadhaar-global-no-face': [
    {
      idDocType: IdDocType.AadhaarGlobalNoFace,
      shape: 'card',
      isGlobal: true,
      skipDiagonal: true,
      skipBack: true
    }
  ],
  /**
   * 表面撮影方式で追加された書類
   * @see https://liquidinc.atlassian.net/wiki/spaces/EKYC/pages/2150629416#%E6%9B%B8%E9%A1%9E%E5%88%A5%E3%81%AE%E6%92%AE%E5%BD%B1%E9%9D%A2%E3%81%8A%E3%82%88%E3%81%B3%E3%83%87%E3%82%B6%E3%82%A4%E3%83%B3
   */
  'foreign-passport': [
    {
      idDocType: IdDocType.ForeignPassportFrontFace,
      shape: 'book',
      isFaceRequired: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.ForeignPassportFront,
      shape: 'book',
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.ForeignPassportFrontBackFace,
      shape: 'book',
      isFaceRequired: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.ForeignPassportFrontBack,
      shape: 'book',
      skipDiagonal: true,
      skipBack: true
    }
  ],
  'employee-id-card': [
    {
      idDocType: IdDocType.EmployeeIDCardFront,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true,
      skipBack: true
    },
    {
      idDocType: IdDocType.EmployeeIDCardFrontBack,
      shape: 'card',
      isGeneral: true,
      skipDiagonal: true
    }
  ]
  // eslint-disable-next-line prettier/prettier
} as const satisfies {[key:string]: readonly IdDocTableItem[]};

const idDocs = Object.values(idDocTable).flat();

export const isGeneralBook = (idDocType: IdDocType): boolean => {
  const generalBookIDs = getGeneralBookIDs();
  return generalBookIDs.includes(idDocType);
};

export const isGeneralCard = (idDocType: IdDocType): boolean => {
  const generalCardIDs = getGeneralCardIDs();
  return generalCardIDs.includes(idDocType);
};

export const isGlobal = (idDocType: IdDocType): boolean => {
  const globalIDs = getGlobalIDs();
  return globalIDs.includes(idDocType);
};

export const isFaceRequired = (idDocType: IdDocType): boolean => {
  const idDoc = idDocs.find((idDoc) => idDoc.idDocType === idDocType);
  if (!idDoc) return false;
  if (!('isFaceRequired' in idDoc)) return false;

  return idDoc.isFaceRequired;
};

export const isSkipDiagonal = (idDocType: IdDocType): boolean => {
  const idDoc = idDocs.find((idDoc) => idDoc.idDocType === idDocType);
  if (!idDoc) return false;
  if (!('skipDiagonal' in idDoc)) return false;

  return idDoc.skipDiagonal;
};

export const isSkipBack = (idDocType: IdDocType): boolean => {
  const idDoc = idDocs.find((idDoc) => idDoc.idDocType === idDocType);
  if (!idDoc) return false;
  if (!('skipBack' in idDoc)) return false;

  return idDoc.skipBack;
};

export const isBookType = (idDocType: IdDocType): boolean => {
  const bookTypeIDs = getBookTypeIDs();
  return bookTypeIDs.includes(idDocType);
};

/**
 * 指定された書類グループに含まれる書類タイプかどうかを返す
 */
export const is = (idDocType: IdDocType, idDocGroup: IdDocGroup): boolean => {
  const groupIDs = getIDsByGroup(idDocGroup);
  return groupIDs.includes(idDocType);
};

export const isPassport = (idDocType: IdDocType): boolean => {
  const passports = [
    'passport',
    'passport-any-issuance-date',
    'passport-global-face',
    'passport-global-no-face',
    'foreign-passport'
  ] as IdDocGroup[];
  const passportIdDocs = passports.map((passport) => idDocTable[passport]).flat();
  return passportIdDocs.some((idDoc) => idDoc.idDocType === idDocType);
};

/**
 * 表面撮影方式（容貌なし）の書類かどうかを返す
 */
export const isFront = (idDocType: IdDocType): boolean => {
  return getFrontIDs().includes(idDocType);
};

/**
 * 表面撮影方式（容貌あり）の書類かどうかを返す
 */
export const isFrontFace = (idDocType: IdDocType): boolean => {
  return getFrontFaceIDs().includes(idDocType);
};

/**
 * 表裏撮影方式の書類かどうかを返す
 */
export const isFrontBack = (idDocType: IdDocType): boolean => {
  return getFrontBackIDs().includes(idDocType);
};

/**
 * 表裏撮影方式（容貌あり）の書類かどうかを返す
 */
export const isFrontBackFace = (idDocType: IdDocType): boolean => {
  return getFrontBackFaceIDs().includes(idDocType);
};

export const getGeneralBookIDs = (): IdDocType[] => {
  return idDocs
    .filter((idDoc) => 'isGeneral' in idDoc && idDoc.isGeneral && idDoc.shape === 'book')
    .map((idDoc) => idDoc.idDocType);
};

export const getGeneralCardIDs = (): IdDocType[] => {
  return idDocs
    .filter((idDoc) => 'isGeneral' in idDoc && idDoc.isGeneral && idDoc.shape === 'card')
    .map((idDoc) => idDoc.idDocType);
};

/**
 * 手帳の形をした書類のID一覧を取得
 */
export const getBookTypeIDs = (): IdDocType[] => {
  return idDocs.filter((idDoc) => idDoc.shape === 'book').map((idDoc) => idDoc.idDocType);
};

export const getGlobalIDs = (): IdDocType[] => {
  return idDocs.filter((idDoc) => 'isGlobal' in idDoc && idDoc.isGlobal).map((idDoc) => idDoc.idDocType);
};

export const getGobalFaceIDs = (): IdDocType[] => {
  return idDocs
    .filter((idDoc) => {
      const isFaceRequired = 'isFaceRequired' in idDoc && idDoc.isFaceRequired;
      const isGlobal = 'isGlobal' in idDoc && idDoc.isGlobal;
      return isFaceRequired && isGlobal;
    })
    .map((idDoc) => idDoc.idDocType);
};

export const getGobalNoFaceIDs = (): IdDocType[] => {
  return idDocs
    .filter((idDoc) => {
      const isFaceRequired = 'isFaceRequired' in idDoc && idDoc.isFaceRequired;
      const isGlobal = 'isGlobal' in idDoc && idDoc.isGlobal;
      return !isFaceRequired && isGlobal;
    })
    .map((idDoc) => idDoc.idDocType);
};

/**
 * 表面撮影方式(容貌あり)書類のID一覧を取得
 * 項目による判定では例外が多すぎるので列挙する
 */
export const getFrontFaceIDs = (): IdDocType[] => {
  return [
    IdDocType.DriversLicenseFrontFace,
    IdDocType.ResidenceCardFrontFace,
    IdDocType.MyNumberCardFrontFace,
    IdDocType.DrivingHistoryCertificateFrontFace,
    IdDocType.SpecialPermanentResidentCertificateFrontFace,
    IdDocType.PassportFrontFace,
    IdDocType.PassportAnyIssuanceDateFrontFace,
    IdDocType.ForeignPassportFrontFace,
    IdDocType.StudentIDCardFrontFace,
    IdDocType.BasicResidentRegistrationCardFrontFace,
    IdDocType.StudentNotebookFrontFace
  ];
};

/**
 * 表面撮影方式(容貌なし)書類のID一覧を取得
 * 項目による判定では例外が多すぎるので列挙する
 */
export const getFrontIDs = (): IdDocType[] => {
  return [
    IdDocType.DriversLicenseFront,
    IdDocType.ResidenceCardFront,
    IdDocType.MyNumberCardFront,
    IdDocType.DrivingHistoryCertificateFront,
    IdDocType.SpecialPermanentResidentCertificateFront,
    IdDocType.PassportFront,
    IdDocType.PassportAnyIssuanceDateFront,
    IdDocType.ForeignPassportFront,
    IdDocType.HealthInsuranceCardFront,
    IdDocType.PhysicalDisabilityCertificateCardFront,
    IdDocType.SpecialEducationCardFront,
    IdDocType.MentalDisabilityCertificateCardFront,
    IdDocType.StudentIDCardFront,
    IdDocType.BasicResidentRegistrationCardFront,
    IdDocType.EmployeeIDCardFront,
    IdDocType.PhysicalDisabilityCertificateBookFront,
    IdDocType.SpecialEducationBookFront,
    IdDocType.MentalDisabilityCertificateBookFront,
    IdDocType.PensionBookFront,
    IdDocType.BasicPensionNumberNotificationFront,
    IdDocType.StudentNotebookFront,
    IdDocType.MotherAndBabyNotebookFront,
    IdDocType.CertificateOfResidenceFront,
    IdDocType.CertificateOfFamilyRegisterFront,
    IdDocType.CertificateOfIndividualRecordsFront
  ];
};

/**
 * 表裏面撮影方式(容貌あり)書類のID一覧を取得
 * 項目による判定では例外が多すぎるので列挙する
 */
export const getFrontBackFaceIDs = (): IdDocType[] => {
  return [
    IdDocType.DriversLicenseFrontBackFace,
    IdDocType.ResidenceCardFrontBackFace,
    IdDocType.MyNumberCardFrontBackFace,
    IdDocType.DrivingHistoryCertificateFrontBackFace,
    IdDocType.SpecialPermanentResidentCertificateFrontBackFace,
    IdDocType.PassportFrontBackFace,
    IdDocType.PassportAnyIssuanceDateFrontBackFace,
    IdDocType.ForeignPassportFrontBackFace,
    IdDocType.StudentIDCardFrontBackFace,
    IdDocType.BasicResidentRegistrationCardFrontBackFace,
    IdDocType.StudentNotebookFrontBackFace
  ];
};

/**
 * 表裏面撮影方式(容貌なし)書類のID一覧を取得
 * 項目による判定では例外が多すぎるので列挙する
 */
export const getFrontBackIDs = (): IdDocType[] => {
  return [
    IdDocType.DriversLicenseFrontBack,
    IdDocType.ResidenceCardFrontBack,
    IdDocType.MyNumberCardFrontBack,
    IdDocType.DrivingHistoryCertificateFrontBack,
    IdDocType.SpecialPermanentResidentCertificateFrontBack,
    IdDocType.PassportFrontBack,
    IdDocType.PassportAnyIssuanceDateFrontBack,
    IdDocType.ForeignPassportFrontBack,
    IdDocType.HealthInsuranceCardFrontBack,
    IdDocType.PhysicalDisabilityCertificateCardFrontBack,
    IdDocType.SpecialEducationCardFrontBack,
    IdDocType.MentalDisabilityCertificateCardFrontBack,
    IdDocType.StudentIDCardFrontBack,
    IdDocType.BasicResidentRegistrationCardFrontBack,
    IdDocType.EmployeeIDCardFrontBack,
    IdDocType.PhysicalDisabilityCertificateBookFrontBack,
    IdDocType.SpecialEducationBookFrontBack,
    IdDocType.MentalDisabilityCertificateBookFrontBack,
    IdDocType.PensionBookFrontBack,
    IdDocType.BasicPensionNumberNotificationFrontBack,
    IdDocType.StudentNotebookFrontBack,
    IdDocType.MotherAndBabyNotebookFrontBack,
    IdDocType.CertificateOfResidenceFrontBack,
    IdDocType.CertificateOfFamilyRegisterFrontBack,
    IdDocType.CertificateOfIndividualRecordsFrontBack
  ];
};

/**
 * 指定の書類グループの書類のID一覧を取得
 */
export const getIDsByGroup = (idDocGroup: IdDocGroup): IdDocType[] => {
  return idDocTable[idDocGroup].map((idDoc) => idDoc.idDocType);
};

export const getShape = (idDocType: IdDocType): IdDocShape => {
  const idDoc = idDocs.find((idDoc) => idDoc.idDocType === idDocType);
  if (!idDoc) return 'unknown';

  return idDoc.shape;
};
